import { EntityType } from '@repo/common/enums/entityType';
import { debounce } from 'lodash-es';
import { makeAutoObservable } from 'mobx';
import type { ISecodaEntity, ITemplate } from '../../api/types';
import { ENTITY_TYPES_MODAL_CONFIG } from '../../components/EntityModal/constants';
import { sanitizeTemplateRichTextField } from '../../components/EntityModal/utils';
import { search } from '../../lib/models/misc';
import { captureError } from '../../web-tracing';
import type {
	CreateEntityModalAttachment,
	CreateEntityModalOnCreateEntityFn,
} from './types';

export class CreateEntityModalStore {
	// for prevent closing check
	initialTitle?: string;
	initialDescription?: string;

	opened = false;
	entityType?: EntityType;
	template?: ITemplate;
	teams?: string[];
	title?: string;
	description?: string;
	metadata: Record<string, unknown> = {};
	attachment?: CreateEntityModalAttachment;
	editorRenderKey = 0;

	// related questions
	relatedQuestions?: ISecodaEntity[];

	showValidationError = false;
	loading = false;

	constructor() {
		makeAutoObservable(this, undefined, {
			autoBind: true,
		});
	}

	open({
		entityType,
		template,
		title,
		description,
		teams,
		metadata,
		attachment,
	}: {
		entityType: EntityType;
		template?: ITemplate;
		title?: string;
		description?: string;
		teams?: string[];
		metadata?: Record<string, unknown>;
		attachment?: CreateEntityModalAttachment;
	}) {
		this.opened = true;
		this.entityType = entityType;

		if (template) {
			this.setTemplate(template, true);
		}

		// Override template properties below if provided
		if (title?.length) {
			this.initialTitle = title;
			this.setTitle(title); // we want to trigger the related questions search if a custom title is provided
		}

		if (description?.length) {
			// We want to place the template description below the custom description
			this.description = `${description}\n\n${this.description ?? ''}`;
			this.initialDescription = this.description;
		}

		if (teams) {
			this.teams = teams;
		}

		if (metadata) {
			this.metadata = metadata;
		}

		if (attachment) {
			this.attachment = attachment;
		}
	}

	reset() {
		this.opened = false;
		this.entityType = undefined;
		this.template = undefined;
		this.teams = undefined;
		this.title = undefined;
		this.description = undefined;
		this.initialTitle = undefined;
		this.initialDescription = undefined;
		this.metadata = {};
		this.relatedQuestions = undefined;
		this.attachment = undefined;
		this.showValidationError = false;
		this.loading = false;
		this.editorRenderKey = 0;
	}

	setTeams(teams: string[]) {
		this.teams = teams;
	}

	async setTemplate(template: ITemplate, overrideProps: boolean) {
		const previousTemplate = this.template;
		this.template = template;

		const templateRichTextValue =
			template?.[
				ENTITY_TYPES_MODAL_CONFIG?.[this.entityType!]?.richTextTemplateField ??
					'definition'
			];
		const previousTemplateRichTextValue =
			previousTemplate?.[
				ENTITY_TYPES_MODAL_CONFIG?.[this.entityType!]?.richTextTemplateField ??
					'definition'
			] ?? '';

		// every time a template is used, we need to refresh the query blocks IDs to make sure they are unique per entity
		const finalDescription = await sanitizeTemplateRichTextField(
			templateRichTextValue ?? ''
		);

		if (overrideProps) {
			this.title = template.title ?? '';
			this.initialTitle = template.title ?? '';
			this.description = finalDescription;
			this.initialDescription = finalDescription;
		} else {
			if (!this.title?.length || this.title === previousTemplate?.title) {
				// ok to override the title
				this.title = template.title ?? '';
				this.initialTitle = template.title ?? '';
			}

			if (
				!this.description?.length ||
				this.description === previousTemplateRichTextValue
			) {
				// ok to override the description
				this.description = finalDescription;
				this.initialDescription = finalDescription;
			} else {
				// user has input something, so we only append the template description to it
				this.description = this.description?.length
					? `${this.description}\n\n${finalDescription}`
					: finalDescription;
			}
		}

		this.teams = template.teams;
		this.setMetadata('parent', template.parent);
		this.setMetadata('published', template.published);
		this.setMetadata('assigned_to', template.assigned_to);
		this.setMetadata('assigned_to_group', template.assigned_to_group);
		this.setMetadata('priority', template.priority);
		this.setMetadata('tags', template.tags);
		this.setMetadata('owners', template.owners);
		this.setMetadata('collections', template.collections);
		this.setMetadata('properties2', template.properties2);
	}

	setTitle(title: string) {
		this.title = title;
		this.showValidationError = true;
		this.findRelatedQuestions();
	}

	get titleValidationError() {
		if (!this.title?.length && this.showValidationError) {
			return 'Title is required';
		}

		if (this.title?.length && this.title.length > 280) {
			return 'Title must be less than 280 characters';
		}

		return null;
	}

	setDescription(description: string) {
		this.description = description;
	}

	setMetadata(key: string, value: unknown) {
		this.metadata[key] = value;
	}

	findRelatedQuestions = debounce(async () => {
		if (this.entityType !== EntityType.question) {
			return;
		}

		try {
			const res = await search({
				search_term: this.title,
				page: 1,
				page_size: 5,
				entity_type: EntityType.question,
			});
			this.relatedQuestions = res.data.results;
		} catch (error) {
			captureError(error);
		}
	}, 300);

	removeAttachment() {
		this.attachment = undefined;
	}

	get hasUnsavedChanges() {
		return (
			!!(
				this.title?.length &&
				this.title?.length > 4 &&
				this.title !== this.initialTitle
			) ||
			!!(
				this.description?.length &&
				this.description?.length > 4 &&
				this.description !== this.initialDescription
			)
		);
	}

	async createEntity(onCreateEntity: CreateEntityModalOnCreateEntityFn) {
		this.showValidationError = true;
		this.loading = true;

		if (this.titleValidationError) {
			return;
		}

		try {
			await onCreateEntity({
				entityType: this.entityType!, // this should never be undefined
				title: this.title,
				description: this.description,
				teams: this.teams,
				metadata: this.metadata,
				attachment: this.attachment,
			});

			this.reset();
		} catch (error) {
			captureError(error);
		} finally {
			this.loading = false;
		}
	}

	triggerEditorRender() {
		this.editorRenderKey += 1;
	}
}

export const createEntityModalStore = new CreateEntityModalStore();
